"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { TradePanel } from "@/components/TradePanel";
import type { MarketDetail } from "@/lib/api";
import { pickLocale } from "@/lib/format";

/**
 * 2026-04-29 — Polymarket-parity mobile UX for /markets/[slug].
 *
 * Two parts:
 *   1. Sticky bottom bar with Buy YES / Buy NO buttons. Always
 *      visible on mobile while scrolling so the primary CTA is
 *      one tap away regardless of where the user is in the page.
 *   2. Bottom-sheet trade composer that slides up when either
 *      button is tapped — wraps the existing TradePanel without
 *      duplicating logic.
 *
 * Hidden on desktop (md:hidden) — the desktop layout already
 * keeps TradePanel visible in the right column. Binary markets
 * only for v1; multi-outcome falls back to the existing column-
 * based composer (filed as follow-up).
 */
// Touch-drag-to-dismiss tuning. 80px translate ≈ a deliberate
// downward drag on a typical mobile screen; 50% velocity threshold
// (px/ms) covers the "flick" gesture even before reaching 80px.
const DISMISS_DRAG_PX = 80;
const DISMISS_FLICK_VELOCITY = 0.5;

export function MobileMarketBottomBar({ market }: { market: MarketDetail }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  // 2026-05-25 — track which side the user tapped so the modal's
  // TradePanel opens with the matching outcome pre-selected. Before
  // this, BUY NO opened the modal but defaulted to YES because both
  // buttons just called setOpen(true).
  const [openSide, setOpenSide] = useState<"yes" | "no">("yes");
  // Live drag offset (sheet translateY in px). 0 = fully open;
  // positive = dragged down. State drives a transform; releasing
  // either snaps back to 0 or animates out + closes.
  const [dragY, setDragY] = useState(0);
  const dragStartRef = useRef<{ y: number; t: number } | null>(null);
  const yesOutcome = market.outcomes.find((o) => o.sortOrder === 0);
  const noOutcome = market.outcomes.find((o) => o.sortOrder === 1);
  const isBinary =
    market.kind === "binary" &&
    yesOutcome?.currentPriceCents !== undefined &&
    noOutcome?.currentPriceCents !== undefined;
  const isMulti = market.kind === "multi_outcome";

  // Multi-outcome bottom bar — defaults to highest-volume outcome,
  // re-binds when a row in <OutcomeRowList /> dispatches the cross-
  // island `granbay.multiOutcome.select` event (same channel used
  // by <MultiOutcomeTradePanel /> on desktop).
  const defaultMultiOutcomeId = useMemo(() => {
    if (!isMulti || market.outcomes.length === 0) return null;
    const sorted = [...market.outcomes].sort((a, b) => {
      const aVol = Number(a.volumeCents ?? 0);
      const bVol = Number(b.volumeCents ?? 0);
      return bVol - aVol;
    });
    return sorted[0]?.id ?? null;
  }, [isMulti, market.outcomes]);
  const [selectedMultiId, setSelectedMultiId] = useState<string | null>(
    defaultMultiOutcomeId,
  );
  useEffect(() => {
    if (!isMulti || typeof window === "undefined") return;
    const onSelect = (e: Event) => {
      const ce = e as CustomEvent<{ outcomeId: string }>;
      if (ce.detail?.outcomeId) setSelectedMultiId(ce.detail.outcomeId);
    };
    window.addEventListener(
      "granbay.multiOutcome.select",
      onSelect as EventListener,
    );
    return () =>
      window.removeEventListener(
        "granbay.multiOutcome.select",
        onSelect as EventListener,
      );
  }, [isMulti]);
  const selectedMultiOutcome = useMemo(
    () =>
      isMulti
        ? (market.outcomes.find((o) => o.id === selectedMultiId) ?? null)
        : null,
    [isMulti, market.outcomes, selectedMultiId],
  );
  const multiYesPrice = selectedMultiOutcome?.currentPriceCents ?? 50;
  const multiNoPrice = 100 - multiYesPrice;
  const multiLabel = selectedMultiOutcome
    ? pickLocale(selectedMultiOutcome.name, "en")
    : null;

  // Lock body scroll while the sheet is open so the page underneath
  // doesn't scroll on touch.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  // Reset drag offset when the sheet (re)opens.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (open) setDragY(0);
  }, [open]);

  // Drag handlers — only active on the drag-handle area, not the
  // whole sheet, so the user can still scroll the trade panel
  // contents normally.
  function onPointerDown(e: React.PointerEvent) {
    e.currentTarget.setPointerCapture(e.pointerId);
    dragStartRef.current = { y: e.clientY, t: performance.now() };
  }

  function onPointerMove(e: React.PointerEvent) {
    if (!dragStartRef.current) return;
    const delta = e.clientY - dragStartRef.current.y;
    // Only allow downward drag — upward gets clamped at 0 so a
    // user can't "pull the sheet up beyond the top" and produce
    // weird whitespace.
    setDragY(Math.max(0, delta));
  }

  function onPointerUp(e: React.PointerEvent) {
    const start = dragStartRef.current;
    dragStartRef.current = null;
    if (!start) return;
    const delta = e.clientY - start.y;
    const dt = Math.max(1, performance.now() - start.t);
    const velocity = delta / dt; // px/ms downward = positive
    // Either dragged far enough or flicked fast enough → dismiss.
    if (delta >= DISMISS_DRAG_PX || velocity >= DISMISS_FLICK_VELOCITY) {
      setOpen(false);
    } else {
      setDragY(0); // snap back
    }
  }

  if (!isBinary && !isMulti) return null;
  if (isMulti && !selectedMultiOutcome) return null;

  const yesPriceDisplay = isMulti
    ? multiYesPrice
    : (yesOutcome?.currentPriceCents ?? 0);
  const noPriceDisplay = isMulti
    ? multiNoPrice
    : (noOutcome?.currentPriceCents ?? 0);

  return (
    <>
      {/* Sticky bottom CTA — mobile only. Hidden on md+ so the
          desktop trade column stays the canonical surface. Multi-
          outcome variant shows the currently-bound outcome above
          the YES/NO buttons so the user knows which outcome they're
          about to trade. */}
      {/* 2026-05-26 — sits ABOVE the global MobileBottomNav (which is
          fixed at bottom:0 with z-40). Both bars sharing bottom:0 made
          them overlap content; the trade bar now docks at
          bottom: 3.5rem (the nav's height) so the two stack cleanly.
          z-50 ensures the trade bar overlays the nav if they ever
          touch. */}
      <div
        className="fixed inset-x-0 z-50 border-t border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 md:hidden"
        style={{
          bottom: "calc(3.5rem + env(safe-area-inset-bottom))",
          paddingBottom: "0.5rem",
        }}
      >
        {isMulti && multiLabel ? (
          <button
            type="button"
            onClick={() => {
              // Open the sheet so the user can both see + switch the
              // currently-bound outcome. The outcome list inside the
              // page is above the fold on mobile, but the user might
              // be scrolled deep into the chart/feed — tapping the
              // chip is a one-tap way back to "I want to trade a
              // different outcome".
              setOpen(true);
              // Cross-island broadcast: OutcomeRowList scrolls itself
              // into view (defined in OutcomeRowList useEffect). No-op
              // if no list is mounted.
              if (typeof window !== "undefined") {
                window.dispatchEvent(
                  new CustomEvent("granbay.multiOutcome.scrollToList"),
                );
              }
            }}
            className="mb-1 flex w-full items-center gap-1 truncate text-left text-[11px] text-[var(--color-text2)] hover:text-[var(--color-text)]"
            aria-label={`Trading ${multiLabel} — tap to change outcome`}
          >
            <span className="text-[var(--color-text3)]">Trading:</span>
            <span className="truncate font-semibold text-[var(--color-text)]">
              {multiLabel}
            </span>
            <span className="ml-auto shrink-0 text-[10px] text-[var(--color-text3)]">
              Change ↕
            </span>
          </button>
        ) : null}
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => {
              setOpenSide("yes");
              setOpen(true);
            }}
            className="flex-1 rounded-md py-3 text-[13px] font-bold text-white transition-opacity active:opacity-80"
            style={{ background: "var(--color-yes)" }}
          >
            {t("mobileMarketBottomBar.buyYes")}{" "}
            <span className="font-mono tabular-nums opacity-90">
              {yesPriceDisplay}¢
            </span>
          </button>
          <button
            type="button"
            onClick={() => {
              setOpenSide("no");
              setOpen(true);
            }}
            className="flex-1 rounded-md py-3 text-[13px] font-bold text-white transition-opacity active:opacity-80"
            style={{ background: "var(--color-no)" }}
          >
            {t("mobileMarketBottomBar.buyNo")}{" "}
            <span className="font-mono tabular-nums opacity-90">
              {noPriceDisplay}¢
            </span>
          </button>
        </div>
      </div>

      {/* Bottom-sheet trade composer — mobile only. Backdrop click
          closes; an explicit Close button covers the case where the
          user just wants out. The drag-handle pill at the top is a
          visual-only Polymarket / iOS sheet affordance — full touch-
          drag-to-dismiss is filed as follow-up because pointer-event
          gesture handling is fiddly enough to warrant its own PR.
          Rendered through a Portal to <body> so it escapes any
          backdrop-filter ancestor (SiteHeader) — same root-cause
          fix as #1387 / #1388. */}
      {open && typeof document !== "undefined"
        ? createPortal(
        <div
          className="fixed inset-0 z-50 flex flex-col md:hidden"
          role="dialog"
          aria-modal="true"
          aria-label={t("mobileMarketBottomBar.tradeComposerAria")}
        >
          <button
            type="button"
            aria-label={t("mobileMarketBottomBar.closeAria")}
            onClick={() => setOpen(false)}
            className="flex-1 cursor-pointer bg-black/60 backdrop-blur-sm"
          />
          <div
            className="max-h-[88vh] overflow-y-auto rounded-t-2xl border-t border-[var(--color-border)] bg-[var(--color-bg)] shadow-[0_-12px_32px_rgba(0,0,0,0.4)]"
            style={{
              paddingBottom: "env(safe-area-inset-bottom)",
              transform: `translateY(${dragY}px)`,
              // No transition while actively dragging (so the sheet
              // tracks the finger 1:1); 200ms ease-out on release so
              // the snap-back / dismiss-out animations feel natural.
              transition:
                // eslint-disable-next-line react-hooks/refs
                dragStartRef.current === null
                  ? "transform 200ms ease-out"
                  : "none",
            }}
          >
            {/* Drag-handle area — pointer events here drive the drag-
                to-dismiss gesture. Tap-and-release with no drag also
                dismisses (matches iOS modal-sheet behaviour). 44px
                tall touch target meets iOS HIG (was 32px before
                2026-05-15 polish pass — bumped without changing the
                visible 4px pill). */}
            <button
              type="button"
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
              onPointerCancel={onPointerUp}
              onClick={() => {
                // Click without a drag (no movement during the
                // pointerdown→pointerup window) still dismisses.
                if (dragY === 0) setOpen(false);
              }}
              aria-label={t("mobileMarketBottomBar.dragHandleAria")}
              className="flex h-11 w-full touch-none items-center justify-center"
            >
              <span
                className="block h-1 w-10 rounded-full"
                style={{ background: "var(--color-text3)", opacity: 0.5 }}
                aria-hidden="true"
              />
            </button>
            <div className="flex items-center justify-between border-b border-[var(--color-border)] px-4 py-2">
              <span className="text-[12px] font-bold uppercase tracking-[0.6px] text-[var(--color-text3)]">
                {t("mobileMarketBottomBar.header")}
              </span>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="text-[12px] font-bold text-[var(--color-text2)]"
              >
                {t("mobileMarketBottomBar.closeButton")}
              </button>
            </div>
            <div className="p-3">
              {isMulti && selectedMultiOutcome ? (
                <TradePanel
                  market={market}
                  boundOutcome={{
                    yesOutcomeId: selectedMultiOutcome.id,
                    noOutcomeId: selectedMultiOutcome.id,
                    yesPriceCents: multiYesPrice,
                    noPriceCents: multiNoPrice,
                    label: multiLabel ?? "",
                  }}
                  initialSide={openSide}
                />
              ) : (
                <TradePanel market={market} initialSide={openSide} />
              )}
            </div>
          </div>
        </div>,
            document.body,
          )
        : null}
    </>
  );
}
